import React, { useEffect, useRef, useState } from "react";
import { X, Camera, Smartphone, RotateCcw, CheckCircle } from "lucide-react";

const CameraCapture = ({ onCapture, onClose, facingMode = "user" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isFrontCamera, setIsFrontCamera] = useState(facingMode === "user");

  // Initialize camera stream
  useEffect(() => {
    let active = true;
    const initCamera = async () => {
      try {
        setError(null);
        setIsLoading(true);

        // Request camera access with specified facing mode (with fallback if high res ideal is not supported)
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: isFrontCamera ? "user" : "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          });
        } catch (e) {
          console.warn("Retrying with simple camera constraints due to error:", e);
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: isFrontCamera ? "user" : "environment",
            },
            audio: false,
          });
        }

        if (!active) {
          // Stream acquired after unmount, stop tracks to prevent leaks
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Ensure video plays and catch potential play promise rejections
          videoRef.current.play().catch((playErr) => {
            console.error("Video play failed:", playErr);
          });
        }
      } catch (err) {
        if (active) {
          console.error("Camera error:", err);
          if (err.name === "NotAllowedError") {
            setError("Camera access denied. Please allow camera permissions.");
          } else if (err.name === "NotFoundError") {
            setError("No camera found on this device.");
          } else if (err.name === "NotReadableError") {
            setError("Camera is already in use by another application.");
          } else {
            setError("Unable to access camera. Please check permissions.");
          }
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    initCamera();

    // Cleanup: stop stream on unmount or camera switch
    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isFrontCamera]);

  // Capture photo from video stream
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Reset transform before drawing
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // If front camera, mirror the canvas horizontally to match user perspective
    if (isFrontCamera) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0);

    // Reset transform back to default
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Get image data and convert to blob
    canvas.toBlob((blob) => {
      if (!blob) {
        setError("Failed to capture image. Try again.");
        return;
      }

      // Create a File object from the blob
      const file = new File([blob], "camera-capture.jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      setCapturedImage(URL.createObjectURL(blob));
      onCapture(file);
    }, "image/jpeg", 0.95);
  };

  // Switch between front and back camera
  const toggleCamera = async () => {
    // Stop current stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    // Switch camera mode and reinitialize
    setIsFrontCamera(!isFrontCamera);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/20 p-2">
              <Camera className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white">Capture Your Photo</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-white hover:bg-white/20 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Camera Container */}
        <div className="relative bg-black overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
              <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-orange-500 border-t-transparent"></div>
                <p className="text-white text-sm font-medium">Initializing camera...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-10 p-4">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="rounded-full bg-red-100/20 p-4 border border-red-500/30">
                  <Camera className="h-8 w-8 text-red-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{error}</p>
                  <p className="text-gray-400 text-xs mt-2">
                    Please check your device permissions and try again.
                  </p>
                </div>
              </div>
            </div>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-96 object-cover ${isFrontCamera ? 'scale-x-[-1]' : ''}`}
          />

          {/* Canvas for capture */}
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Capture frame overlay */}
          {!capturedImage && !error && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-80 border-2 border-orange-400 rounded-3xl shadow-2xl shadow-orange-500/30"></div>
            </div>
          )}

          {/* Preview overlay */}
          {capturedImage && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-orange-500">
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="h-96 w-auto max-w-sm object-cover"
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex gap-3 justify-between sm:justify-end flex-wrap">
          <div className="flex gap-2">
            {!capturedImage && !error && (
              <button
                onClick={toggleCamera}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white transition font-medium text-sm bg-white shadow-sm"
              >
                <Smartphone className="h-4 w-4" />
                Switch Camera
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {capturedImage ? (
              <>
                <button
                  onClick={() => setCapturedImage(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white transition font-medium text-sm bg-white shadow-sm flex items-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Retake
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 transition font-semibold text-sm shadow-lg flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Use Photo
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white transition font-medium text-sm bg-white shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCapture}
                  disabled={isLoading || !!error}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 transition font-semibold text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Capture
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
